
    export default {
      mounted() {
        this.input = this.el.querySelector("[data-combobox-input]");
        this.panel = this.el.querySelector("[data-combobox-panel]");
        this.activeKey = null;

        // Focus that comes back to the input after a pick (ours, or LiveView restoring it after a
        // patch) must not reopen the panel; the user's next click, keystroke or leaving does.
        this.input.addEventListener("focus", () => this.justSelected || this.open());
        this.input.addEventListener("click", () => this.open());
        this.input.addEventListener("input", () => this.open());
        this.input.addEventListener("keydown", (e) => this.onKeydown(e));
        this.el.addEventListener("change", (e) => {
          if (e.target.matches("[data-combobox-panel] input[type=radio]")) this.onSelected(e.target);
        });
        // A pick from inside the form makes LiveView hold the patch back until after this
        // event arrives, a clear doesn't; dispatch once the DOM shows the announced change.
        this.handleEvent("combobox:changed", ({ id, change }) => {
          if (id !== this.el.id) return;
          this.pendingChange = String(change);
          this.dispatchChangeIfPatched();
        });
        this.panel.addEventListener("mousedown", (e) => e.preventDefault());
        // Choosing the option that is already selected fires no `change`, so close here instead.
        this.panel.addEventListener("click", (e) => {
          const option = e.target.closest("[role=option]");
          const radio = option && option.querySelector("input[type=radio]");
          if (radio && radio.checked && !e.target.matches("input")) this.onSelected(radio);
        });
        this.panel.addEventListener("mousemove", (e) => {
          const item = e.target.closest("[data-combobox-item]");
          if (item && !item.hasAttribute("data-active")) this.setActive(item, { scroll: false });
        });
        this.el.addEventListener("focusout", (e) => {
          if (this.el.contains(e.relatedTarget)) return;
          if (e.relatedTarget) this.justSelected = false;
          this.close();
        });

        this.onOutsidePointerDown = (e) => {
          if (this.el.contains(e.target)) return;
          this.justSelected = false;
          this.close();
        };
        this.onViewportChange = () => this.isOpen() && this.position();
        document.addEventListener("pointerdown", this.onOutsidePointerDown);
        window.addEventListener("resize", this.onViewportChange);
        window.addEventListener("scroll", this.onViewportChange, true);
        if (document.activeElement === this.input) this.open();
      },
      updated() {
        this.dispatchChangeIfPatched();
        this.restoreActive();
        if (this.isOpen()) this.position();
      },
      destroyed() {
        document.removeEventListener("pointerdown", this.onOutsidePointerDown);
        window.removeEventListener("resize", this.onViewportChange);
        window.removeEventListener("scroll", this.onViewportChange, true);
      },
      dispatchChangeIfPatched() {
        if (this.pendingChange === undefined || this.pendingChange !== this.el.dataset.change) return;
        this.pendingChange = undefined;
        this.input.value = this.el.dataset.display;
        this.el.querySelector("[data-combobox-value]").dispatchEvent(new Event("input", { bubbles: true }));
        const focused = document.activeElement;
        if (focused !== this.input && (focused === document.body || this.el.contains(focused))) this.input.focus();
      },
      isOpen() {
        return this.panel.matches(":popover-open");
      },
      open() {
        this.justSelected = false;
        if (this.isOpen()) return;
        this.side = "below";
        this.panel.showPopover();
        this.input.setAttribute("aria-expanded", "true");
        this.position();
        this.restoreActive();
      },
      close() {
        if (!this.isOpen()) return;
        this.panel.hidePopover();
        this.input.setAttribute("aria-expanded", "false");
        this.setActive(null);
        if (this.input.value !== this.el.dataset.display) {
          this.input.value = this.el.dataset.display;
          this.pushEventTo(this.el, "search", { query: "" });
        }
      },
      // Revisit once CSS anchor positioning (anchor-name / position-anchor) is supported in all
      // browsers we target: it could place the popover without measuring the input here.
      // Opens below the input, or above it when it doesn't fit below and there is more room
      // above, and shrinks to the room on that side. It stays on its side while it still fits
      // there, so narrowing the list doesn't make it jump.
      position() {
        const gap = 2;
        const viewportMargin = 8;
        const rect = this.input.parentElement.getBoundingClientRect();
        const viewportHeight = document.documentElement.clientHeight;
        const room = {
          below: viewportHeight - rect.bottom - gap - viewportMargin,
          above: rect.top - gap - viewportMargin,
        };
        Object.assign(this.panel.style, {
          position: "fixed",
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          maxHeight: "",
        });
        const height = this.panel.offsetHeight;
        const other = this.side === "above" ? "below" : "above";
        if (height > room[this.side] && room[other] > room[this.side]) this.side = other;
        const available = Math.max(room[this.side], 0);
        Object.assign(this.panel.style, {
          top: this.side === "below" ? `${rect.bottom + gap}px` : "auto",
          bottom: this.side === "above" ? `${viewportHeight - rect.top + gap}px` : "auto",
          maxHeight: height > available ? `${available}px` : "",
        });
      },
      items() {
        return Array.from(this.panel.querySelectorAll("[data-combobox-item]")).filter((item) =>
          item.checkVisibility(),
        );
      },
      keyOf(item, items) {
        return item.id || `idx:${items.indexOf(item)}`;
      },
      setActive(item, { scroll = true } = {}) {
        const items = this.items();
        items.forEach((i) => i.removeAttribute("data-active"));
        this.activeKey = item ? this.keyOf(item, items) : null;
        if (!item) {
          this.input.removeAttribute("aria-activedescendant");
          return;
        }
        item.setAttribute("data-active", "");
        if (item.id) this.input.setAttribute("aria-activedescendant", item.id);
        if (scroll) item.scrollIntoView({ block: "nearest" });
      },
      restoreActive() {
        if (!this.isOpen()) return;
        const items = this.items();
        if (this.pendingIndex !== undefined && items.length > this.pendingIndex + 1) {
          const index = this.pendingIndex;
          this.pendingIndex = undefined;
          return this.setActive(items[index]);
        }
        const current = items.find((i) => this.keyOf(i, items) === this.activeKey);
        const checked = items.find((i) => i.querySelector("input[type=radio]:checked"));
        const preferred = items.find((i) => i.hasAttribute("data-combobox-preferred"));
        this.setActive(current || checked || preferred || items[0] || null);
      },
      move(delta) {
        const items = this.items();
        if (items.length === 0) return;
        const index = items.findIndex((i) => this.keyOf(i, items) === this.activeKey);
        const next = index === -1 ? (delta > 0 ? 0 : items.length - 1) : (index + delta + items.length) % items.length;
        this.setActive(items[next]);
      },
      onKeydown(e) {
        switch (e.key) {
          case "ArrowDown":
          case "ArrowUp":
            e.preventDefault();
            if (!this.isOpen()) return this.open();
            return this.move(e.key === "ArrowDown" ? 1 : -1);
          case "Home":
          case "End":
            if (!this.isOpen()) return;
            e.preventDefault();
            const items = this.items();
            return this.setActive(e.key === "Home" ? items[0] : items[items.length - 1]);
          case "Enter":
            e.preventDefault();
            if (!this.isOpen()) return this.open();
            const items2 = this.items();
            const active = items2.find((i) => this.keyOf(i, items2) === this.activeKey);
            if (active && active.matches("[data-combobox-load-more]")) this.pendingIndex = items2.indexOf(active);
            return active && active.click();
          case "Escape":
            if (this.isOpen()) {
              e.preventDefault();
              this.close();
            }
            return;
        }
      },
      onSelected(radio) {
        this.el.dataset.display = radio.closest("[data-label]").dataset.label;
        this.close();
        this.justSelected = true;
        this.input.focus();
      },
    };
  